import classNames from 'classnames';
import Hammer from 'hammerjs';
import { prefix } from 'inline-style-prefixer';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { DOC_TYPE_ALIAS } from '../../../config/helpers';
import { imagePropTypes } from '../../../propTypes';
import { above, below, between } from '../../../util/mediaQuery';

import {
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';
import Icon from '../Atoms/Icon';
import ErrorBoundary from '../Core/ErrorBoundary';
import Picture from '../Picture';
import PromotedPackage from './PromotedPackage';
import PromotedPage from './PromotedPage';
import CUSTOM_EVENT from '../../../util/customEvents';
import { pricePerCurrencyType } from '../../../util/sharedPropTypes';

const propTypes = exact({
  callToBookText: PropTypes.string,
  compareCtaText: PropTypes.string.isRequired,
  ctaText: PropTypes.string,
  fromPriceText: PropTypes.string,
  image: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      documentTypeAlias: PropTypes.string.isRequired,
      image: imagePropTypes,
      isCallToBook: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      limitedAvailability: PropTypes.bool.isRequired,
      limitedAvailabilityText: PropTypes.string,
      limitedAvailabilityRemainingSpaces: PropTypes.string,
      pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType),
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      waitingListLink: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        isNewWindow: PropTypes.bool,
      }),
    })
  ).isRequired,
  limitedAvailabilityText: PropTypes.string,
  perPersonText: PropTypes.string,
  soldOutText: PropTypes.string.isRequired,
  title: PropTypes.string,
  waitingListText: PropTypes.string,
});

const defaultProps = {
  callToBookText: 'Call To Book',
  ctaText: 'More',
  fromPriceText: 'FROM',
  image: null,
  perPersonText: 'pp',
  title: '',
  limitedAvailabilityText: '',
  waitingListText: null,
};

class PromotedCarousel extends Component {
  constructor(props) {
    super(props);

    this.handleWindowResize = null;
    this.carousel = null;
    this.touchManager = null;

    this.addTouchSwipe = this.addTouchSwipe.bind(this);
    this.calculateGroups = this.calculateGroups.bind(this);
    this.handleGroupRequest = this.handleGroupRequest.bind(this);
    this.handleSwipeLeft = this.handleSwipeLeft.bind(this);
    this.handleSwipeRight = this.handleSwipeRight.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);

    this.isCurrencyAvailable = !!props.items[0]?.pricePerCurrency;
    const currentCurrency = this.isCurrencyAvailable
      ? getInitialCurrencyState(props.items[0].pricePerCurrency)
      : null;

    this.state = {
      activeGroupIndex: 0,
      currentCurrencyCodeAndSymbol: currentCurrency,
      groupCount: null,
      itemsPerGroup: null,
    };
  }

  componentDidMount() {
    this.calculateGroups();

    this.handleWindowResize = debounce(this.calculateGroups, 1000 / 30);
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    setTimeout(this.addTouchSwipe, 0);
  }

  componentWillUnmount() {
    if (this.handleWindowResize) {
      window.removeEventListener('resize', this.handleWindowResize);
    }

    if (this.touchManager) {
      this.touchManager.off('swipeleft');
      this.touchManager.off('swiperight');
    }
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    if (!this.isCurrencyAvailable) return;
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.items[0].pricePerCurrency,
      newCurrencyCode
    );

    if (newCurrencyPriceSetting)
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
      });
    else console.error('Currency not found from current currency chosen to currency from model');
  }

  setGroups(itemsPerGroup) {
    if (itemsPerGroup === this.state.itemsPerGroup) return;

    const groupCount = Math.ceil(this.props.items.length / itemsPerGroup);
    this.setState({ itemsPerGroup, groupCount });
  }

  calculateGroups() {
    if (below('tablet')) this.setGroups(1);
    if (between('tablet', 'desktop')) this.setGroups(2);
    if (above('desktop')) this.setGroups(3);
  }

  addTouchSwipe() {
    if (!this.carousel) return;
    this.touchManager = new Hammer.Manager(this.carousel, { touchAction: 'pan-y' });
    const swipe = new Hammer.Swipe();

    this.touchManager.add(swipe);
    this.touchManager.on('swipeleft', this.handleSwipeLeft);
    this.touchManager.on('swiperight', this.handleSwipeRight);
  }

  handleGroupRequest(index) {
    this.setState({ activeGroupIndex: index });
  }

  handleSwipeLeft() {
    if (this.state.activeGroupIndex >= this.state.groupCount - 1) return;
    this.handleGroupRequest(this.state.activeGroupIndex + 1);
  }

  handleSwipeRight() {
    if (this.state.activeGroupIndex <= 0) return;
    this.handleGroupRequest(this.state.activeGroupIndex - 1);
  }

  render() {
    if (!this.state.itemsPerGroup || !this.state.groupCount) return null;

    const navigationItems = [...Array(this.state.groupCount)].map((navigationItem, index) => ({
      id: index,
      itemClasses: classNames('PromotedCarousel_navigationItem', {
        active: index === this.state.activeGroupIndex,
      }),
    }));

    const itemClasses = classNames(
      'PromotedCarousel_item',
      `PromotedCarousel_item-groupsOf${this.state.itemsPerGroup}`
    );

    const listStyles = prefix({
      transform: `translate3d(-${this.state.activeGroupIndex * 100}%, 0, 0)`,
    });

    return (
      <section className="PromotedCarousel">
        {this.props.image && (
          <Picture
            {...this.props.image}
            className="PromotedCarousel_image"
            sizes={[
              [100, 0.5], // from 0
              [100, 0.64], // from 480
              [100, 1], // from 768
              [100, 1.4], // from 1024
              [100, 2], // from 1440
            ]}
          />
        )}
        <div className="PromotedCarousel_container">
          <ErrorBoundary>
            <h2 className="PromotedCarousel_title">{this.props.title}</h2>
            <div
              className="PromotedCarousel_content"
              ref={(element) => {
                this.carousel = element;
              }}
            >
              <ul className="PromotedCarousel_list" style={listStyles}>
                {this.props.items.map((item) => {
                  const currencyPriceSetting = this.isCurrencyAvailable
                    ? findCurrencyItemFromListWithFallback(
                        item.pricePerCurrency,
                        this.state.currentCurrencyCodeAndSymbol?.code
                      )
                    : null;
                  return (
                    <li key={item.id} className={itemClasses}>
                      {item.documentTypeAlias === DOC_TYPE_ALIAS.TOUR && (
                        <PromotedPackage
                          callToBookText={this.props.callToBookText}
                          compareCtaText={this.props.compareCtaText}
                          currencySymbol={this.state.currentCurrencyCodeAndSymbol?.symbol}
                          ctaText={this.props.ctaText}
                          date={item.date}
                          description={item.description}
                          fromPriceText={this.props.fromPriceText}
                          hasLimitedAvailability={item.limitedAvailability}
                          image={item.image}
                          inclusions={item.inclusions}
                          isCallToBook={item.isCallToBook}
                          isSoldOut={item.isSoldOut}
                          tourKey={item.key}
                          limitedAvailabilityText={this.props.limitedAvailabilityText}
                          limitedAvailabilityTextOverride={item.limitedAvailabilityText}
                          limitedAvailabilityRemainingSpaces={
                            item.limitedAvailabilityRemainingSpaces
                          }
                          price={currencyPriceSetting?.price}
                          perPersonText={this.props.perPersonText}
                          title={item.title}
                          soldOutText={this.props.soldOutText}
                          tournamentName={item.tournamentName}
                          url={item.url}
                          waitingListLink={item.waitingListLink}
                          waitingListText={this.props.waitingListText}
                        />
                      )}
                      {item.documentTypeAlias !== DOC_TYPE_ALIAS.TOUR && (
                        <PromotedPage
                          ctaText={this.props.ctaText}
                          description={item.description}
                          documentTypeAlias={item.documentTypeAlias}
                          image={item.image}
                          title={item.title}
                          url={item.url}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            {this.state.groupCount > 1 && (
              <ul className="PromotedCarousel_navigationList">
                <button
                  type="button"
                  onClick={() => this.handleSwipeRight()}
                  className={`PromotedCarousel_arrow${
                    this.state.activeGroupIndex === 0 ? ' PromotedCarousel_arrow-hidden' : ''
                  }`}
                >
                  <Icon id="arrow-left" />
                </button>
                {navigationItems.map((navigationItem, index) => (
                  <li key={navigationItem.id} className={navigationItem.itemClasses}>
                    <button
                      aria-label={`Carousel button slide number ${index + 1}`}
                      type="button"
                      className="PromotedCarousel_navigationButton"
                      onClick={() => this.handleGroupRequest(index)}
                    />
                  </li>
                ))}
                <button
                  type="button"
                  onClick={() => this.handleSwipeLeft()}
                  className={`PromotedCarousel_arrow${
                    this.state.activeGroupIndex === this.state.groupCount - 1
                      ? ' PromotedCarousel_arrow-hidden'
                      : ''
                  }`}
                >
                  <Icon id="arrow-right" />
                </button>
              </ul>
            )}
          </ErrorBoundary>
        </div>
      </section>
    );
  }
}

PromotedCarousel.propTypes = propTypes;
PromotedCarousel.defaultProps = defaultProps;

export default PromotedCarousel;
